import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import PostCard from '../../Jobs/JobPosts/JobPostCard/PostCard';
import CreateJobForm from './CreateJobForm';
import AIJobCreationChat from './AIJobCreationChat';
import './jobsView.css';
import Loading from '../Loading/Loading';
import { useFetchJobPosts } from '../../../hooks/company/useFetchJobPosts';
import useCurrentRestaurant from '../../../hooks/useCurrentRestaurant';

const JobsView = ({ layoutType }) => {
  const [showCreateJobForm, setShowCreateJobForm] = useState(false);
  const [showAIChat, setShowAIChat] = useState(false);
  const [flashMessage, setFlashMessage] = useState(null);
  const topRef = useRef(null);
  const processedFlashRef = useRef(null); // Track processed flash messages
  const location = useLocation();
  const { restaurantId } = useCurrentRestaurant();
  const { jobPosts, isLoading, error, refetch } = useFetchJobPosts(restaurantId);

  const displayFlashMessage = (message) => {
    setFlashMessage(message);
    setTimeout(() => {
      setFlashMessage(null);
    }, 3000);
  };

  const handleCreateJobClick = () => {
    setShowCreateJobForm(!showCreateJobForm);
    setShowAIChat(false);
  };

  const handleCreateWithAIClick = () => {
    setShowAIChat(!showAIChat);
    setShowCreateJobForm(false);
  };

  const handleJobCreated = useCallback((message) => {
    refetch();
    displayFlashMessage(message);
    setShowCreateJobForm(false);
    setShowAIChat(false);
    topRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [refetch]);

  // Handle flash message from navigation (after editing)
  useEffect(() => {
    const flashMsg = location.state?.flashMessage;
    const locationKey = location.key;
    
    // Only process if we have a flash message and haven't processed this location key yet
    if (flashMsg && processedFlashRef.current !== locationKey) {
      processedFlashRef.current = locationKey;
      
      // Clear state immediately to prevent re-processing
      window.history.replaceState({}, document.title);
      
      // Use a small delay to ensure state is cleared before refetching
      const timeoutId = setTimeout(() => {
        refetch();
        displayFlashMessage(flashMsg);
        setShowCreateJobForm(false);
        setShowAIChat(false);
        topRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      
      return () => {
        clearTimeout(timeoutId);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]); // Only depend on location.key to prevent multiple triggers

  const handleJobDeleted = (message) => {
    displayFlashMessage(message);
    topRef.current?.scrollIntoView({ behavior: 'smooth' });
    refetch();
  };

  if (isLoading) {
    return <><Loading/></>;
  }

  // Always show the interface with create job button, even if there's an error or no jobs
  return (
    <>
      <div ref={topRef}>
        {flashMessage && (
          <div className="flash-message">
            <p>{flashMessage}</p>
          </div>
        )}

        <div className="jobs-view__container">
          <h1>Ofertas de trabajo</h1>
          <div className="jobs-view__buttons">
            <button className="jobs-view__add-button" onClick={handleCreateJobClick}>
              {showCreateJobForm ? 'Ir a tus trabajos' : '+ Crear Trabajo'}
            </button>
            {/* <button className="jobs-view__ai-button" onClick={handleCreateWithAIClick}>
              {showAIChat ? 'Ir a tus trabajos' : '🤖 Crear con IA'}
            </button> */}
          </div>

          {showCreateJobForm ? (
            <CreateJobForm
              onJobCreated={handleJobCreated}
            />
          ) : showAIChat ? (
            <AIJobCreationChat
              onJobCreated={handleJobCreated}
              onClose={() => setShowAIChat(false)}
            />
          ) : (
            <div>
              {error && jobPosts.length === 0 ? (
                <div className="no-jobs-message">
                  <p>No tienes trabajos publicados aún. ¡Crea tu primera oferta de trabajo!</p>
                </div>
              ) : jobPosts.length === 0 ? (
                <div className="no-jobs-message">
                  <p>No tienes trabajos publicados aún. ¡Crea tu primera oferta de trabajo!</p>
                </div>
              ) : (
                jobPosts.map((post) => (
                  <PostCard
                    key={post.id}
                    data={post}
                    type={layoutType}
                    onDelete={handleJobDeleted}
                  />
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default JobsView;
